import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { ColumnDef } from '@tanstack/react-table';
import { EstimateStatus } from './EstimateStatus';
import EstimateStatusCell from '@/components/estimates/EstimateStatusCell';
import { format } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { Translator } from '../translator.type';

export type EstimateEditableCircuit = {
  version: number;
  title: string | null;
  name: string | null;
  originalCircuit: {
    code: number;
  };
};

export const formatEditableCircuitCode = (
  editableCircuit: EstimateEditableCircuit,
) =>
  `C${editableCircuit.originalCircuit.code.toString().padStart(4, '0')}-${editableCircuit.version}`;

export const formatEditableCircuitName = (
  editableCircuit: EstimateEditableCircuit,
) => editableCircuit.title ?? editableCircuit.name ?? '-';

export type Estimate = {
  id: string;
  ref: number;
  circuit: {
    id: string;
    code: number;
    name: string;
    durationDays: number;
    durationNights: number;
    company: {
      id: string;
      name: string;
    };
    currencies: string[];
    updatedAt: string;
  } | null;
  editableCircuit?: EstimateEditableCircuit | null;
  arrivalDate: string | null;
  amount: number | null;
  originalAmount?: number | null;
  name: string | null;
  marginRate: number | null;
  effectiveMarginRate?: number | null;
  totalMarginAmount?: number | null;
  /** Agency overlay: only populated by the backend in the agency-link context. */
  agencyAdditionalMarginType?: 'FIXED' | 'PERCENT' | null;
  agencyAdditionalMarginValue?: number | null;
  agencyBaseCurrency?: string;
  change: number;
  status: EstimateStatus;
  createdAt: string;
  updatedAt: string;
  autoUpdatedAt: string | null;
  EstimateCircuit?: {
    paragraphStates?: Record<string, string> | null;
  } | null;
  reservationAgent: {
    id: string;
    firstname: string;
    lastname: string;
  };
  _count: {
    adults: number;
    children: number;
  };
};

export const ESTIMATES_COLUMN_DEF = (t: Translator): ColumnDef<Estimate>[] => [
  {
    accessorKey: 'ref',
    header: t('estimates.columns.ref'),
    cell: ({ row }) => (
      <span>{`D${row.original.ref.toString().padStart(3, '0')}`}</span>
    ),
  },
  {
    accessorKey: 'name',
    header: t('estimates.columns.name'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-32 xl:max-w-48 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.name ? (
              <span>{row.original.name}</span>
            ) : (
              <span>-</span>
            )}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'circuit.code',
    header: t('estimates.columns.circuit'),
    cell: ({ row }) => {
      const { editableCircuit, circuit } = row.original;
      if (editableCircuit) {
        return <span>{formatEditableCircuitCode(editableCircuit)}</span>;
      }
      return circuit?.code ? (
        <span>{`C${circuit.code.toString().padStart(4, '0')}`}</span>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    accessorKey: 'circuit.name',
    header: t('estimates.columns.circuitName'),
    cell: ({ row }) => {
      const circuitName = row.original.editableCircuit
        ? formatEditableCircuitName(row.original.editableCircuit)
        : (row.original.circuit?.name ?? '-');
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              className="max-w-32 xl:max-w-48 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
              aria-label="nom"
            >
              {circuitName}
            </TooltipTrigger>
            <TooltipContent
              className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
              aria-label="nom complet"
            >
              {circuitName}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    accessorKey: 'circuit.durationDays',
    header: t('estimates.columns.duration'),
    cell: ({ row }) =>
      row.original.circuit?.durationDays &&
      row.original.circuit.durationNights ? (
        <span>
          {row.original.circuit.durationDays}j
          {row.original.circuit.durationNights &&
            ` / ${row.original.circuit.durationNights}n`}
        </span>
      ) : row.original.circuit?.durationDays ? (
        <span>{row.original.circuit.durationDays}j</span>
      ) : (
        <span>-</span>
      ),
  },
  {
    accessorKey: '_count.adults',
    header: t('estimates.columns.participants'),
    cell: ({ row }) => (
      <span>{row.original._count.adults + row.original._count.children}</span>
    ),
  },
  {
    accessorKey: 'arrivalDate',
    header: t('estimates.columns.arrivalDate'),
    cell: ({ row }) =>
      row.original.arrivalDate ? (
        <span>{format(new Date(row.original.arrivalDate), 'dd/MM/yyyy')}</span>
      ) : (
        <span>-</span>
      ),
  },
  {
    accessorKey: 'amount',
    header: t('estimates.columns.amount'),
    cell: ({ row }) =>
      row.original.amount ? (
        <span>{`${formatPrice(row.original.amount)} €`}</span>
      ) : (
        '-'
      ),
  },
  {
    accessorKey: 'effectiveMarginRate',
    header: t('estimates.columns.marginRate'),
    cell: ({ row }) => {
      const rate =
        row.original.effectiveMarginRate ?? row.original.marginRate ?? null;
      return rate != null ? <span>{`${rate}%`}</span> : <span>-</span>;
    },
  },
  {
    accessorKey: 'totalMarginAmount',
    header: t('estimates.columns.marginAmount'),
    cell: ({ row }) =>
      row.original.totalMarginAmount != null ? (
        <span>{`${formatPrice(row.original.totalMarginAmount)} €`}</span>
      ) : (
        '-'
      ),
  },
  {
    id: 'additionalMargin',
    header: t('estimates.columns.additionalMargin'),
    cell: ({ row }) => {
      const {
        agencyAdditionalMarginType,
        agencyAdditionalMarginValue,
        amount,
      } = row.original;
      if (agencyAdditionalMarginValue == null) return <span>-</span>;
      if (agencyAdditionalMarginType === 'PERCENT') {
        // Prefer the resolved € amount when the (already-margined) total is
        // available on the row; otherwise fall back to the raw percentage.
        return amount != null ? (
          <span>{`${formatPrice((amount * agencyAdditionalMarginValue) / 100)} €`}</span>
        ) : (
          <span>{`${agencyAdditionalMarginValue}%`}</span>
        );
      }
      return <span>{`${formatPrice(agencyAdditionalMarginValue)} €`}</span>;
    },
  },
  {
    accessorKey: 'circuit.company.name',
    header: t('estimates.columns.supplier'),
  },
  {
    accessorKey: 'circuit.originalAmount',
    header: t('estimates.columns.totalCurrency'),
    cell: ({ row }) => {
      const currency = row.original.circuit?.currencies[0];

      return (
        <span>
          {row.original.originalAmount
            ? `${formatPrice(row.original.originalAmount)} ${formatCurrency(currency ?? 'EUR')}`
            : '-'}
        </span>
      );
    },
  },
  {
    accessorKey: 'change',
    header: t('estimates.columns.change'),
    cell: ({ row }) => {
      const quoteCurrency = row.original.circuit?.currencies?.[0];
      const baseCurrency = row.original.agencyBaseCurrency ?? 'EUR';
      const { change } = row.original;

      if (!quoteCurrency || change == null) return <span>-</span>;
      if (quoteCurrency === baseCurrency) return <span>1</span>;

      const formattedRate = change.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });

      return (
        <span>{`${baseCurrency}/${quoteCurrency} ${formattedRate}`}</span>
      );
    },
  },
  {
    accessorKey: 'reservationAgent.firstname',
    header: t('estimates.columns.agent'),
  },
  {
    accessorKey: 'createdAt',
    header: t('estimates.columns.createdAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.createdAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: t('estimates.columns.updatedAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'status',
    header: t('estimates.columns.status'),
    cell: ({ row }) => <EstimateStatusCell status={row.original.status} />,
  },
];
